import React, { useMemo } from 'react';
import { Billboard, Text } from '@react-three/drei';
import { ItemAlmacen3D } from './tipos';

interface Pallet3DProps {
  position: [number, number, number];
  color: string;
  items: ItemAlmacen3D[];
  cantidadTotal: number;
  isDamaged?: boolean;
  isReserved?: boolean;
  anchoSlot?: number;
  escala?: number;
}

export const Pallet3D: React.FC<Pallet3DProps> = ({
  position,
  color,
  items,
  isDamaged = false,
  isReserved = false,
  anchoSlot,
  escala,
}) => {
  // Escala adaptativa según ancho del slot (para evitar colisión si hay 8 posiciones)
  const finalScale = useMemo(() => {
    if (escala) return escala;
    if (anchoSlot) {
      return Math.min(1.0, (anchoSlot * 0.9) / 1.15);
    }
    return 1.0;
  }, [escala, anchoSlot]);

  // Pallet dimensiones estándar (en metros): 1.15 x 0.9 x 0.12 base + carga de 0.7m
  const cargoColor = useMemo(() => {
    if (isDamaged) return '#dc2626'; // Rojo brillante para cuarentena/daño
    if (isReserved) return '#3b82f6'; // Azul reservado
    return color;
  }, [isDamaged, isReserved, color]);

  // Etiqueta legible del producto principal (ej: COCA)
  const primerItem = items && items.length > 0 ? items[0] : null;
  const nombreEtiqueta = primerItem?.nombre ? primerItem.nombre.slice(0, 10).toUpperCase() : primerItem?.sku || '';

  return (
    <group position={position} scale={[finalScale, finalScale, finalScale]}>
      {/* 1. Base del Pallet (Madera de Pinho/Tarima) */}
      {/* Tablas superiores del pallet */}
      <mesh position={[0, 0.08, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.15, 0.03, 0.9]} />
        <meshStandardMaterial color="#8B5A2B" roughness={0.9} metalness={0.05} />
      </mesh>

      {/* Tacos / Bloques de madera intermedios (9 bloques representados por 3 vigas de soporte) */}
      <mesh position={[-0.48, 0.04, 0]} castShadow>
        <boxGeometry args={[0.12, 0.06, 0.88]} />
        <meshStandardMaterial color="#6B4423" roughness={0.95} />
      </mesh>
      <mesh position={[0, 0.04, 0]} castShadow>
        <boxGeometry args={[0.12, 0.06, 0.88]} />
        <meshStandardMaterial color="#6B4423" roughness={0.95} />
      </mesh>
      <mesh position={[0.48, 0.04, 0]} castShadow>
        <boxGeometry args={[0.12, 0.06, 0.88]} />
        <meshStandardMaterial color="#6B4423" roughness={0.95} />
      </mesh>

      {/* Tablas inferiores del pallet */}
      <mesh position={[0, 0.01, 0]} receiveShadow>
        <boxGeometry args={[1.15, 0.02, 0.9]} />
        <meshStandardMaterial color="#7a4b22" roughness={0.9} />
      </mesh>

      {/* 2. Carga / Cajas Paletizadas (Envoltorio film stretch) */}
      <group position={[0, 0.46, 0]}>
        {/* Bloque principal de carga */}
        <mesh castShadow receiveShadow>
          <boxGeometry args={[1.08, 0.72, 0.84]} />
          <meshStandardMaterial
            color={cargoColor}
            roughness={0.45}
            metalness={0.1}
          />
        </mesh>

        {/* Film transparente / Borde de empaque */}
        <mesh>
          <boxGeometry args={[1.09, 0.73, 0.85]} />
          <meshStandardMaterial
            color="#ffffff"
            transparent
            opacity={0.12}
            roughness={0.1}
          />
        </mesh>

        {/* Etiqueta de despacho / Guía WMS (Frente) */}
        <mesh position={[0.3, 0.1, 0.426]}>
          <planeGeometry args={[0.22, 0.14]} />
          <meshBasicMaterial color="#ffffff" />
        </mesh>
        {/* Código de barras simulado en la etiqueta */}
        <mesh position={[0.3, 0.08, 0.427]}>
          <planeGeometry args={[0.16, 0.05]} />
          <meshBasicMaterial color="#1e293b" />
        </mesh>

        {/* Nombre del producto / SKU visible en el frente de la carga */}
        {nombreEtiqueta && (
          <Billboard position={[0, 0.08, 0.435]}>
            <Text
              fontSize={0.14}
              color="#ffffff"
              anchorX="center"
              anchorY="middle"
              fontWeight="bold"
              outlineWidth={0.015}
              outlineColor="#0f172a"
            >
              {nombreEtiqueta}
            </Text>
          </Billboard>
        )}

        {/* Flejes de seguridad (Zunchos negros de sujeción) */}
        <mesh position={[-0.3, 0, 0]}>
          <boxGeometry args={[0.02, 0.74, 0.86]} />
          <meshBasicMaterial color="#0f172a" />
        </mesh>
        <mesh position={[0.3, 0, 0]}>
          <boxGeometry args={[0.02, 0.74, 0.86]} />
          <meshBasicMaterial color="#0f172a" />
        </mesh>
      </group>
    </group>
  );
};
